import {
	HubTemplateSaveModel,
	EditHubTemplateComposerViewModel,
	getEditViewModel,
	update
} from "client/Api/Hub/Templates/hubTemplateManagement";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	EMPTY_ARRAY,
	isNotNil
} from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	cleanUpMedia,
	convertLayoutToSaveModel,
	convertMediaToSaveModel,
	createCommonMediaObject,
	getOriginalImagesFromLayout
} from "../../converters";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback,
	useUnmountedCancellation} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import {
	Dialog,
	Form,
	FormattedText,
	Toolbar,
	useConfirmationDialog
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { InputValidationResult, InputValidationResultKind } from "client/Validation";
import {
	FormData,
	HubTemplateFormContent,
	HubTemplateFormContentHandle,
	hubTemplateFormValidationConfiguration
} from "./HubTemplateFormContent";
import { never } from "client_legacy/ndaw/errors";
import { HubItemImageRestrictionsModel, HubItemMediaKind } from "client/Api/Hub/common";
import {
	BlobIdImageElementComposerViewModel,
	BlobIdImageHubMediaComposerViewModel,
	ExistingHubItemImages,
	HubImageMediaKind
} from "client/Api/Hub/common.composer";
import { IObjectValidatorBuilder } from "client/Validation/builder";
import { createLayoutValidation, createMediaValidation } from "../../Builder/validation";
import { DocumentLocationViewModel } from "client/Api/Hub/hubSettings";

export type EditHubTemplateDialogProps = {
	templateId: number;
	onClose(): void;
	onSave(): void;
	onBack?(): void;
};

export const EditHubTemplateDialog = (props: EditHubTemplateDialogProps): ReactElement => {
	const {
		templateId,
		onBack,
		onSave,
		onClose: onCloseFromProps
	} = props;

	const onClose: () => void = useStableImperativeCallback(onCloseFromProps);

	const [formData, setFormData] = useState<FormData>(defaultFormData);
	const [initialFormData, setInitialFormData] = useState<FormData>(defaultFormData);
	const [state, setState] = useState<State>(defaultState);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const alertManager: AlertManager = useAlerts();

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();

	const isDataChanged = useMemo(
		(): boolean => isNotNil(initialFormData)
			&& !deepComparison(formData, initialFormData),
		[
			formData,
			initialFormData
		]
	);

	const {
		isLoaded,
		name,
		imageRestrictions = {
			maxImageSize: 0,
			supportedFileExtensions: EMPTY_ARRAY,
			supportedMimeTypes: EMPTY_ARRAY
		},
		defaultImageLocation = null,
		originalImages,
		kind,
		isPixabayImageGalleryAvailable = false
	} = state;

	const validationConfiguration = useMemo(
		(): IObjectValidatorBuilder<FormData> => {
			const validation: IObjectValidatorBuilder<FormData> = hubTemplateFormValidationConfiguration.clone();

			validation.forObject("media")
				.addValidators(
					createMediaValidation(
						imageRestrictions.supportedMimeTypes,
						imageRestrictions.maxImageSize
					)
				);

			validation.forObject("layout")
				.addValidators(
					createLayoutValidation(
						imageRestrictions.supportedMimeTypes,
						imageRestrictions.maxImageSize,
						true
					)
				);

			return validation;
		},
		[
			imageRestrictions.maxImageSize,
			imageRestrictions.supportedMimeTypes
		]
	);

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const contentRef: RefObject<HubTemplateFormContentHandle | null> = useRef<HubTemplateFormContentHandle>(null);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchTemplate(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);
					const viewModel: EditHubTemplateComposerViewModel = await getEditViewModel(
						templateId,
						signal
					);

					const saveModel: FormData = await convertToHubTemplateSaveModel(viewModel);

					setFormData(saveModel);
					setInitialFormData(saveModel);

					const {
						name,
						imageRestrictions,
						defaultImageLocation,
						kind,
						isPixabayImageGalleryAvailable
					} = viewModel;

					setState({
						isLoaded: true,
						name,
						imageRestrictions,
						defaultImageLocation,
						originalImages: getHubTemplateOriginalImages(viewModel),
						kind,
						isPixabayImageGalleryAvailable
					});

					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			}

			const cancellation = new AbortController();

			fetchTemplate(cancellation.signal);

			return (): void => cancellation.abort();
		},
		[
			alertManager,
			onClose,
			templateId
		]
	);

	const onBeforeUnload = useCallback(
		(event: BeforeUnloadEvent) => {
			const isChanged: boolean = isDataChanged
				|| (
					contentRef.current
						?.getIsContentChanged() ?? false
				);

			if (isChanged) {
				beforeUnloadListener(event);
			}
		},
		[isDataChanged]
	);

	useEffect(
		() => {
			window
				.addEventListener(
					"beforeunload",
					onBeforeUnload
				);

			return () => window
				.removeEventListener(
					"beforeunload",
					onBeforeUnload
				);
		},
		[onBeforeUnload]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				const validationResult: InputValidationResult = await formHandler.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				await update(
					templateId,
					prepareHubTemplateForSaving(formData),
					globalCancellation
				);
				alertManager.showSuccess(localize("_SuccessfullySavedAsTemplate"));

				onSave();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);

				if (formHandler.validation.tryProcessServerError(e)) {
					return;
				}

				alertManager.showError(localize("_ChangesNotSaved"));
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			templateId,
			onSave
		]
	);

	const checkIfCanNavigateAway = useCallback(
		async (): Promise<boolean> => {
			const hasChanges: boolean = isDataChanged
				|| (
					contentRef.current
						?.getIsContentChanged() ?? false
				);

			if (!hasChanges) {
				return true;
			}

			const leaveConfirmation: TryResult<void> = await openConfirmationDialog({
				title: localize("_AreYouSureYouWantToLeaveThisPage"),
				children: localize("_YouHaveUnsavedChanges"),
				confirmText: localize("_Leave"),
				cancelText: localize("_Stay"),
				trackingId: "discard-edit-hub-template-form-confirmation-dialog"
			});

			return leaveConfirmation.wasSuccessful;
		},
		[isDataChanged, openConfirmationDialog]
	);

	const backHandler = useMemo(
		(): (() => void) | undefined => {
			if (!onBack) {
				return undefined;
			}

			return async (): Promise<void> => {
				if (await checkIfCanNavigateAway()) {
					onBack();
				}
			};
		},
		[checkIfCanNavigateAway, onBack]
	);

	const closeHandler = useCallback(
		async (): Promise<void> => {
			if (await checkIfCanNavigateAway()) {
				onClose();
			}
		},
		[checkIfCanNavigateAway, onClose]
	);

	return (
		<>
			<Dialog
				variant="left-slider"
				onClose={closeHandler}
				hasBusyIndicator={isBusy}
				trackingId="edit-hub-template-dialog"
			>
				<Dialog.Header
					title={
						<FormattedText
							text={localize("_EditNameTemplate")}
							thingsToReplace={[
								{
									what: "{{Name}}",
									filler: name
								}
							]}
						/>
					}
					onClose={closeHandler}
					onBack={backHandler}
					hasBorder
				/>
				<Dialog.Body goesEdgeToEdge>
					<Form
						height="fill"
						onSubmit={saveHandler}
					>
						{isLoaded && (
							<HubTemplateFormContent
								attachable={formHandler}
								hubItemKind={kind}
								imageRestrictions={imageRestrictions}
								defaultImageLocation={defaultImageLocation}
								originalImages={originalImages}
								ref={contentRef}
								isPixabayImageGalleryAvailable={isPixabayImageGalleryAvailable}
							/>
						)}
					</Form>
				</Dialog.Body>
				<Dialog.Footer hasBorder>
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							text={localize("_Save")}
							onClick={saveHandler}
							trackingId="save"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
			{confirmationDialog}
		</>
	);
};
EditHubTemplateDialog.displayName = "EditHubTemplateDialog";

export type EditHubTemplateDialogOpenData = Omit<EditHubTemplateDialogProps, "onClose" | "onSave">
	& (
		| {
			closeDialogOn?: "close";
			onClose?: undefined;
		}
		| {
			closeDialogOn: "back";
			onClose(): void;
		}
	);

export function useEditHubTemplateDialog(): [
	element: ReactElement | null,
	open: (openData: EditHubTemplateDialogOpenData) => Promise<boolean>
] {
	const [
		element,
		originalOpen
	] = useDataInputDialog<EditHubTemplateDialogOpenData, void>(
		(data, onClose, onSave) => {
			const {
				closeDialogOn: closeDialog,
				onBack,
				onClose: onCloseFromProps,
				...props
			} = data;

			const closeHandler = (): void => {
				switch (closeDialog) {
					case "close":
					case undefined:
						onClose();
						break;
					case "back":
						onCloseFromProps();
						break;
					default:
						never(closeDialog);
				}
			};

			const backHandler = isNotNil(onBack) || closeDialog === "back"
				? (): void => {
					if (closeDialog === "back") {
						onClose();
					}

					onBack?.();
				}
				: undefined;

			return (
				<EditHubTemplateDialog
					{...props}
					onClose={closeHandler}
					onBack={backHandler}
					onSave={onSave}
				/>
			);
		}
	);

	const openHandler = useCallback(
		async (openData: EditHubTemplateDialogOpenData): Promise<boolean> => {
			return (await originalOpen(openData))
				.wasSuccessful;
		},
		[originalOpen]
	);

	return [element, openHandler];
}

const defaultFormData: FormData = {
	title: "",
	media: {
		...createCommonMediaObject(),
		mediaKind: HubItemMediaKind.None
	},
	layout: {
		rows: []
	}
};

async function convertToHubTemplateSaveModel(viewModel: EditHubTemplateComposerViewModel): Promise<FormData> {
	return {
		title: viewModel.title ?? "",
		layout: isNotNil(viewModel.layout)
			? await convertLayoutToSaveModel(viewModel.layout)
			: { rows: [] },
		media: await convertMediaToSaveModel(viewModel.media)
	};
}

export function getHubTemplateOriginalImages(viewModel: EditHubTemplateComposerViewModel): ExistingHubItemImages {
	const {
		media
	} = viewModel;

	const coverImage: BlobIdImageHubMediaComposerViewModel | null = media.mediaKind === HubItemMediaKind.Image
		&& media.imageKind === HubImageMediaKind.BlobId
			? media
			: null;

	const layoutImages: Map<number, BlobIdImageElementComposerViewModel> = isNotNil(viewModel.layout)
		? getOriginalImagesFromLayout(viewModel.layout)
		: new Map();

	return {
		coverImage,
		layoutImages
	};
}

export function prepareHubTemplateForSaving(model: FormData): HubTemplateSaveModel {
	return {
		...model,
		media: cleanUpMedia(model.media)
	};
}

type State = {
	isLoaded: true;
	name: string;
	imageRestrictions: HubItemImageRestrictionsModel;
	defaultImageLocation: DocumentLocationViewModel | null;
	originalImages: ExistingHubItemImages;
	kind: HubItemKind;
	isPixabayImageGalleryAvailable: boolean;
} | {
	isLoaded: false;
	name?: undefined;
	imageRestrictions?: undefined;
	defaultImageLocation?: undefined;
	originalImages?: undefined;
	kind?: undefined;
	isPixabayImageGalleryAvailable?: undefined;
};

const defaultState: State = {
	isLoaded: false
};

function beforeUnloadListener(event: BeforeUnloadEvent): string {
	event.preventDefault();
	event.returnValue = localize("_YouHaveUnsavedChanges");
	return event.returnValue;
};